import { formatDateTime } from '@/utils/common';
import { handleDownload } from '@/utils/common';
import {
    ArrowLeftOutlined,
    DownloadOutlined,
    UploadOutlined
} from '@ant-design/icons';
import { css } from '@emotion/react';
import { Button, message, Row, Table, TableColumnsType, Tabs, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    getHistoryProduct,
    importFile,
    watchListHistory,
    watchProductPaginationHistory,
    watchProductLoading,
    watchProductMessage,
    resetMessages,
    downloadTemplateProduct
} from '@/redux/slices/product.slice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { setMessages } from '@/redux/slices/app.slice';

type ProductByListTableProps = {
    setActiveTabProps: React.Dispatch<React.SetStateAction<string>>;
};

type PageChangeParams = {
    page?: number;
    pageSize?: number;
};

export default function ProductByListTable({
    setActiveTabProps
}: ProductByListTableProps) {
    const [activeTab, setActiveTab] = useState<string>('INPUT');
    const dispatch = useAppDispatch();
    const pagination = useAppSelector(watchProductPaginationHistory);
    const listHistory = useAppSelector(watchListHistory);
    const loading = useAppSelector(watchProductLoading);
    const messageInfo = useAppSelector(watchProductMessage);

    const [fileName, setFileName] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        dispatch(getHistoryProduct({}));
    }, [dispatch]);

    const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            setFileName(file.name);
            setSelectedFile(file);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.name.endsWith('.zip')) {
              message.error('Chỉ chấp nhận file .zip!');
              return;
            }
            setFileName(file.name);
            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile);
        dispatch(importFile(formData));

    };

    const handleCancel = () => {
        setFileName(null);
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    useEffect(() => {
        if (messageInfo) {
            dispatch(
                setMessages([
                    {
                        type: messageInfo.type,
                        message: messageInfo.message,
                        description: messageInfo.description
                    }
                ])
            );

            if (messageInfo.type === 'success') {
                handleCancel();
                setActiveTabProps('TABLE');
            }
            dispatch(resetMessages());
        }
    }, [dispatch, messageInfo, setActiveTabProps]);
    const tabItems = [
        {
            key: 'INPUT',
            label: 'Nhập dữ liệu'
        },
        {
            key: 'HISTORY',
            label: 'Lịch sử nhập dữ liệu'
        }
    ];

    const columns: TableColumnsType<any> = [
        {
            title: 'STT',
            render: (_, __, index) => index + 1,
            width: 100
        },
        {
            title: 'Tên file',
            dataIndex: 'name',
            key: 'name',
            width: 400
        },
        {
            title: 'Tải xuống',
            dataIndex: 'url',
            key: 'url',
            render: (url: string, record: any) => {
                return (
                    <div
                        css={css`
              display: flex;
              justify-content: center;
              align-items: center;

              .ant-btn {
                border: none;
              }
            `}
                    >
                        <Button icon={<DownloadOutlined />}
                            onClick={() => handleDownload(url, record.name)}
                        ></Button>
                    </div>
                );
            },
            width: 100
        },
        {
            title: 'Ngày nhập dữ liệu',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 200,
            render: (record) => (record ? formatDateTime(record) : '')
        },
        {
            title: 'Người nhập',
            dataIndex: 'createdBy',
            key: 'createdBy',
            width: 200
        }
    ];

    const handlePageChange = useCallback(
        ({
            page = PAGINATION_PARAMS.offset,
            pageSize = PAGINATION_PARAMS.limit,
        }: PageChangeParams) => {
            dispatch(getHistoryProduct({
                offset: page,
                limit: pageSize
            }));
        },
        [dispatch]
    );

    const handleDownLoadTemplateProduct = async () => {
        const template = await dispatch(downloadTemplateProduct()).unwrap();
        if (template) {
            handleDownload(template.URL, template.NAME);
        }
    }

    return (
        <div css={rootStyles}>
            <Row css={boxStyles} align='middle' gutter={16}>
                <Button
                    type='text'
                    icon={<ArrowLeftOutlined />}
                    onClick={() => setActiveTabProps('TABLE')}
                ></Button>
                <Typography.Title level={5} style={{ marginBottom: 0 }}>
                    Nhập sản phẩm theo danh sách
                </Typography.Title>
            </Row>

            <Tabs
                css={tabStyle}
                defaultActiveKey='INPUT'
                onChange={(key) => setActiveTab(key)}
            >
                {tabItems.map((item) => (
                    <Tabs.TabPane tab={item.label} key={item.key} />
                ))}
            </Tabs>

            {activeTab === 'INPUT' && (
                <>
                    <div css={boxStyles}>
                        <Row>
                            <Button type='primary' icon={<UploadOutlined />}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Tải lên
                            </Button>
                            <Button type='link'
                                onClick={() => handleDownLoadTemplateProduct()}
                            >
                                Tải xuống template
                            </Button>
                        </Row>
                    </div>
                    <div css={boxStyles}>
                        <label
                            htmlFor="file-upload"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleDrop}
                        >
                            <div
                                style={{
                                    minHeight: '60vh',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                            >
                                <Typography.Title level={5}>
                                    {fileName ? `Đã chọn: ${fileName}` : 'Vui lòng kéo thả hoặc chọn thư mục dữ liệu danh sách sản phẩm'}
                                </Typography.Title>
                            </div>
                            <input
                                id="file-upload"
                                type="file"
                                accept=".zip"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                                ref={fileInputRef}
                            />
                        </label>
                    </div>
                    <div css={boxFooterStyles}>
                        <Button type='default' onClick={handleCancel}>Hủy</Button>
                        <Button type='primary' onClick={handleUpload}>Lưu</Button>
                    </div>
                </>
            )}
            {activeTab === 'HISTORY' && (
                <Table<any>
                    columns={columns}
                    dataSource={listHistory}
                    loading={loading}
                    pagination={{
                        showQuickJumper: true,
                        showSizeChanger: true,
                        current: pagination.curPage,
                        pageSize: pagination.limitPage,
                        total: pagination.totalRows,
                        onChange: (page, pageSize) => {
                            handlePageChange({ page, pageSize });
                        }
                    }}
                />
            )}
        </div>
    );
}

const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 1rem;
  min-height: calc(100vh - var(--header-height));

  .ant-table-thead > tr > th {
    background: var(--blue-dark-color) !important;
    color: var(--white-color);
  }

  .ant-table-cell::before {
    display: none;
  }
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const boxFooterStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  .ant-btn {
    width: 25rem;
  }
`;

const tabStyle = css`
  .ant-tabs-nav {
    margin-bottom: 0;
  }

  .ant-tabs-tab {
    padding: 6px 8px;
    color: #333;
    border: 1px solid #ccc;
    background-color: #f0f0f0;
  }

  .ant-tabs-tab-active {
    background-color: var(--blue-dark-color);
    border: 1px solid var(--blue-dark-color);

    .ant-tabs-tab-btn {
      color: #ffffff !important;
    }
  }

  .ant-tabs-ink-bar {
    display: none;
  }

  .ant-tabs-tab:hover {
    color: #333 !important;
  }

  .ant-tabs-tab:not(:first-of-type) {
    margin: 0 0 0 8px !important;
  }
`;
